/**
 * Order-ops — the write-intent channels the orders destination fires
 * (resend, fix-email, check-in, undo check-in, refund, purge) and the permission
 * gates that decide which of them an operator sees at all. Pure; a web-side
 * `.sync.ts` is the sole answerer, mirroring admin/sale/guest-issue.state.ts's
 * own action/result channel shape.
 *
 * `canCheckInTickets`/`canUndoCheckIn` gate the same
 * `tickets.checkin.scan`/`tickets.checkin.undo` permission strings
 * `@momo/views/checkin`'s `checkin.state.ts` gates for the door — read only
 * the permission string, with no `status === "member"` or `actor.mfa`
 * check. That's deliberate, not an oversight: the Orders-tab button they
 * gate only decides whether to render an affordance behind an already
 * `tickets.reports.view`-gated console route, where the underlying
 * Firestore CAS write is the actual enforcement (`ticket.entity.ts`'s
 * `redeem`/`unredeem` rules require `me.mfa.eq(true)` regardless of which FE
 * predicate rendered the button). Collapsing the two would either weaken
 * the door's own stricter gate or newly hide this button from a
 * `reports.view`-holding, non-mfa'd session — neither is this file's call.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { Order, type Refund, type RefundBatch, type Ticket } from "@momo/types";
import { Subject } from "rxjs";
import { activeActor } from "../../auth/actor.state.ts";
import {
	consoleEventId,
	consoleOrders,
	consoleOrdersStatus,
	consoleRowsAnswered,
	consoleTickets,
	consoleTicketsStatus,
	type OrderRow,
	type TicketRow,
} from "./console.state.ts";
import { orderTotal } from "./order-detail.engine.ts";

/** Whether the signed-in actor may amend a placed order's buyer-facing
 *  fields — mirrors root package.json's ican.extraMatches buyerEmail-fix
 *  write rule (tickets.orders.amend). Gates the Fix email action only; Resend
 *  rides tickets.reports.view alone, the floor for opening the orders
 *  destination at all. */
export const canAmendOrders = computed(
	() =>
		activeActor.get()?.permissions.includes("tickets.orders.amend") ?? false,
);

/** Whether the actor may admit a ticket from this surface — the same
 *  `tickets.checkin.scan` the door itself rides. */
export const canCheckInTickets = computed(
	() =>
		activeActor.get()?.permissions.includes("tickets.checkin.scan") ?? false,
);

/** Reversing an admission is supervisor authority beyond ordinary scanning,
 *  and it reaches any admission rather than only one this account made — the
 *  phone working the door carries `tickets.checkin.scan` alone, so a
 *  reversal is always somebody else's scan being corrected. It rides its own
 *  `tickets.checkin.undo` (packages/types/src/permissions.ts explains why the
 *  catalog carries both). */
export const canUndoCheckIn = computed(
	() =>
		activeActor.get()?.permissions.includes("tickets.checkin.undo") ?? false,
);

/** Whether the actor may destroy an order outright — `tickets.orders.purge`,
 *  the authority no other token names: refunding money and erasing the record
 *  of it are different acts. The callable re-checks the same permission
 *  server-side and additionally refuses any order that is neither
 *  `payment.env === "sandbox"`, nor `origin === "guest"`, nor priced at
 *  nothing by its own lines — the three grounds that carry no money — so this
 *  predicate decides only whether an affordance is rendered, never what the
 *  purge may reach. */
export const canPurgeOrders = computed(
	() =>
		activeActor.get()?.permissions.includes("tickets.orders.purge") ?? false,
);

/** Which order's sheet is open, written by `<order-detail>` on connect and
 *  cleared when it leaves. The one thing a `.sync` cannot read off the sheet
 *  itself: the sheet is mounted through `show()` with its order as a property,
 *  so nothing outside it names the order on screen. Its own refund ledger is
 *  keyed on this cell rather than on `orderRefundSelection`, which is non-null
 *  only while a refund is being composed — a plain order open would otherwise
 *  never read the ledger at all, and every cancelled ticket on it would sit
 *  under an answer nobody asked for. */
export const openOrderId = state<string | null>("order-ops/open-order").memory(
	null,
);

export const orderEmailSettled$ = new Subject<{
	readonly requestId: string;
	readonly outcome: "queued" | "updated" | "sendRefused" | "refused";
}>();

export const orderResendRequested$ = new Subject<{
	readonly requestId?: string;
	readonly eventId: string;
	readonly orderId: string;
}>();

export const orderActionSettled$ = new Subject<{
	readonly requestId: string;
	readonly message: string | null;
}>();

export const ticketCheckinRequested$ = new Subject<{
	readonly requestId?: string;
	readonly eventId: string;
	readonly ticketId: string;
	readonly typeName: string;
}>();

export const ticketUndoCheckinRequested$ = new Subject<{
	readonly requestId?: string;
	readonly eventId: string;
	readonly ticketId: string;
	readonly typeName: string;
}>();

/** The purge write-intent channel — `web`'s order-ops.sync.ts is the
 *  sole answerer, calling the purgeOrder callable. The operator's
 *  acknowledgement happens before anything is pushed here, so an emission is
 *  already a confirmed intent. */
export const orderPurgeRequested$ = new Subject<{
	readonly requestId?: string;
	readonly eventId: string;
	readonly orderId: string;
}>();

/** Every sandbox order of the mounted event, in the list's own order — the
 *  selection the erase-all gesture acts on, derived once here so the confirm
 *  states a count and the `.sync` drives exactly those ids rather than
 *  re-deriving a selection that may have moved between the two beats. Null
 *  when no event is mounted; an empty `orderIds` when the event holds no test
 *  order, which is what keeps the affordance absent rather than disabled. */
export const eventPurgeDraft = computed<{
	readonly eventId: string;
	readonly orderIds: readonly string[];
} | null>(() => {
	const eventId = consoleEventId.value;
	if (!eventId) return null;
	return {
		eventId,
		orderIds: consoleOrders.value
			.filter((o) => o.payment?.env === "sandbox")
			.map((o) => o.id),
	};
});

/** The bulk purge write-intent channel — one emission per confirmed gesture,
 *  carrying the orders that confirm named. `web`'s order-ops.sync.ts
 *  is the sole answerer and drives the SAME per-order callable
 *  `orderPurgeRequested$` drives, once per id and sequentially, so there is no
 *  second destructive server path and one order's refusal stays that order's. */
export const orderPurgeAllRequested$ = new Subject<{
	readonly requestId?: string;
	readonly eventId: string;
	readonly orderIds: readonly string[];
}>();

export const orderFixEmailRequested$ = new Subject<{
	readonly requestId?: string;
	readonly eventId: string;
	readonly orderId: string;
	readonly newEmail: string;
}>();

/**
 * orderId → an object URL for that order's stored invoice, once a `.sync` has
 * fetched it. The PDF cannot be framed straight from Storage: Chrome refuses
 * to render a cross-origin PDF inside an iframe — verified side by side, the
 * same document blank from the storage host and drawn correctly from a
 * same-origin `blob:` — so the bytes must reach the page's own origin before
 * the browser's viewer will draw them. Fetching is I/O and belongs to the
 * `.sync`; the view reads the resolved URL and shows no frame until one
 * arrives.
 */
export const orderInvoicePreviews = state<Readonly<Record<string, string>>>(
	"order-ops/invoice-previews",
).memory({});

export const orderInvoicePreviewRequested$ = new Subject<{
	readonly orderId: string;
	readonly url: string;
}>();

// ── Refunds ──────────────────────────────────────────────────────────────────

/** Whether the actor may give money back — the same `tickets.orders.refund`
 *  the emitted `refundBatches` create rule enforces, so a member who cannot
 *  write never sees the control. The rule additionally requires `me.mfa`,
 *  which is not restated here: `sessionReady` (auth/session.state.ts) already
 *  gates the whole admin shell on `actor.mfa === true`, so inside this console
 *  the two predicates cannot disagree. */
export const canRefundOrders = computed(
	() =>
		activeActor.get()?.permissions.includes("tickets.orders.refund") ?? false,
);

/** One entry of a batch's `selection`, derived from the contract rather than
 *  restated — the console and the fan-out read one shape. */
export type RefundSelectionEntry = RefundBatch.RefundBatch["selection"][number];

/** Which order's tickets the operator is choosing between, and which of them
 *  are chosen. A null `orderId` is the refund step being closed — the sheet
 *  reads its own mode off this cell rather than a local field, so one place
 *  says whether a refund is being composed. */
export type OrderRefundSelection = {
	readonly orderId: string | null;
	readonly ticketIds: readonly string[];
};

export const orderRefundSelection = state<OrderRefundSelection>(
	"order-ops/refund-selection",
).memory({ orderId: null, ticketIds: [] });

/** One row of an order's own refund ledger — the entity plus the claim id its
 *  path carries. */
export type RefundRow = Refund.Refund & { readonly id: string };

/** The open order's `refunds` subcollection as a `.sync` last read it. Null
 *  while no answer has arrived for the order being composed against, and that
 *  distinction is load-bearing rather than a loading nicety: the consumer
 *  subtracts every ticket ANY existing ledger row names before it derives its
 *  own working set, and answers an empty remainder with `heldByUnresolvedClaim`
 *  — retryable, never `done`. A draft composed before the answer arrives would
 *  therefore price and offer tickets no money can follow, and the operator's
 *  resubmit would move nothing while reading as accepted. Written only by
 *  web's order-ops.sync.ts. */
export type OrderRefundLedger = {
	readonly orderId: string;
	readonly rows: readonly RefundRow[];
};

export const orderRefundLedger = state<OrderRefundLedger | null>(
	"order-ops/refund-ledger",
).memory(null);

/** Why one ticket cannot be part of a refund. The two that come off the ticket
 *  itself are the ticket's own non-`valid` states, derived from the entity
 *  rather than re-listed here, so a status the model grows is a refusal this
 *  surface must answer for rather than a token it silently drops. The other two
 *  are facts the ticket document cannot carry: `claimed` for a ticket an
 *  unresolved ledger row already holds, `unpriced` for one `perTicketShares`
 *  refused to price because its line's ticket count disagrees with its `qty`. */
export type RefundTicketRefusal =
	| Exclude<Ticket.Ticket["status"], "valid">
	| "claimed"
	| "unpriced";

/** What the refund step needs to render one ticket row. The ticket itself, not
 *  a flattened copy of the two fields this screen happens to read today —
 *  every state the entity tracks stays visible to the surface reasoning over
 *  it.
 *
 *  `share` is what this seat gives back if the operator presses the button —
 *  never a figure that cannot move. It starts as the seat's pro-rata share of
 *  what was paid (its face share less its part of any voucher discount) and
 *  stops there on every order that still holds every cent its seats are
 *  worth; where an earlier refund over-drew, what the order has left is what
 *  the seats a refund can still reach share out, and this is that seat's part
 *  of it.
 *
 *  `faceShare` is what the ticket itself was priced at. The two are equal on
 *  every order nobody discounted and nobody over-drew, which is what keeps
 *  such a row rendering one figure; where they differ the row leads with
 *  `share` and strikes `faceShare` under it, so "but the ticket says 15,00 €"
 *  is answered without arithmetic. */
export type RefundTicketRow = {
	readonly ticket: TicketRow;
	readonly share: number | null;
	readonly faceShare: number | null;
	readonly refusal: RefundTicketRefusal | null;
	readonly selected: boolean;
};

/** Why a composed draft cannot be sent as it stands. Null is the one sendable
 *  state, and `selection` is non-empty exactly then.
 *
 *  A working set worth more than the order can still give back is NOT one of
 *  them, and can no longer arise: every row is priced against what the order
 *  has left before it is offered, so any set of them adds up to something the
 *  order can actually pay. An operator refused outright here is one who cannot
 *  give a paying customer their remaining seats back at all. */
export type RefundBlock = "ledgerPending" | "nothingSelected";

/** The open order's refund step: either a composed draft, or the one refusal
 *  this surface can hit — a session that is not admitted to the event's
 *  tickets. That session can still create the batch (the two rides are
 *  different permissions), so the step states plainly that it cannot show what
 *  the refund would void rather than rendering every consequence as zero. */
export type OrderRefundDraft =
	| {
			readonly kind: "composing";
			readonly order: OrderRow;
			readonly rows: ReadonlyMap<string, RefundTicketRow>;
			readonly selectedCount: number;
			/** How many rows a refund could name at all — the ones carrying no
			 *  refusal. Zero is an order with nothing left to give back, which is
			 *  what tells the surface there is no whole-order gesture to offer. */
			readonly selectableCount: number;
			/** Whether every one of those is already chosen, so the whole-order
			 *  control says which way it moves rather than making the operator
			 *  count. */
			readonly allSelectableSelected: boolean;
			/** Whether a seat of this order gives back its printed price or
			 *  something less, and which code took the difference — the one
			 *  verdict the sheet says out loud, once, rather than per row. */
			readonly pricing: Order.RefundPricing;
			/** What the chosen seats give back — the plain sum of their own rows'
			 *  figures, and nothing else. The rows ARE the money: no second
			 *  derivation stands beside them free to disagree with what the operator
			 *  is reading, and the intent carries this figure as its `amount`, so the
			 *  consumer's own clamp is a ceiling that never binds rather than a
			 *  second opinion about one order. */
			readonly payoutCents: number;
			/** What the order can still give back: its own gross, less what has
			 *  already come back, less what claims still in flight have committed —
			 *  the consumer's own arithmetic, mirrored so the two agree. */
			readonly headroomCents: number;
			/** What the action bar owes a sentence for, and at most one thing:
			 *  only where a voucher makes it non-obvious, the assurance that taking
			 *  every seat reconciles to exactly what was paid. Null is the ordinary
			 *  case — every row and the button now state the same figures, so there
			 *  is no difference between them left to explain away. */
			readonly footnote: "wholeOrderReconciles" | null;
			readonly block: RefundBlock | null;
			readonly selection: readonly RefundSelectionEntry[];
	  }
	| { readonly kind: "ticketsUnreadable"; readonly order: OrderRow };

/** What an order can still give back: its own gross, less what has already
 *  come back, less what claims still in flight have committed — the refund
 *  consumer's own arithmetic, mirrored here so the two agree. One home for it,
 *  read by the composer that prices against it and by the money block that
 *  states it, because two spellings of one figure are free to disagree about
 *  an order while an operator is deciding how much to return. */
function headroomOf(order: OrderRow, ledgerRows: readonly RefundRow[]): number {
	return Math.max(
		0,
		orderTotal(order) -
			(order.refundedAmount ?? 0) -
			ledgerRows
				.filter((r) => r.status === "claiming")
				.reduce((sum, r) => sum + r.amount, 0),
	);
}

/** What the open order's money block says about what has already come back.
 *  Three states and never an absence: the line is always filled, so a refund
 *  settling while the sheet is open changes a sentence rather than pushing the
 *  ticket list down. Null only where there is no such line to write — no sheet
 *  open, its order not loaded, or an order that billed nothing at all. */
export type OrderRefundStanding = { readonly orderId: string } & (
	| { readonly kind: "checking" }
	| { readonly kind: "untouched" }
	| { readonly kind: "partlyReturned"; readonly headroomCents: number }
);

export const openOrderRefundStanding = computed<OrderRefundStanding | null>(
	() => {
		const orderId = openOrderId.value;
		if (orderId === null) return null;
		const order = consoleOrders.value.find((o) => o.id === orderId);
		if (order === undefined || orderTotal(order) === 0) return null;
		const ledger = orderRefundLedger.value;
		if (ledger?.orderId !== orderId) return { orderId, kind: "checking" };
		const headroomCents = headroomOf(order, ledger.rows);
		return headroomCents === orderTotal(order)
			? { orderId, kind: "untouched" }
			: { orderId, kind: "partlyReturned", headroomCents };
	},
);

/** Null whenever there is nothing to compose: no order chosen, the order or
 *  its event's tickets not loaded, an order that holds no captured payment to give back — the same
 *  `status: 'paid'` floor the consumer enforces — or a guest-origin order,
 *  which is staff-issued with no real captured payment to give back, so the
 *  sheet never offers a refund the marker would refuse. */
export const orderRefundDraft = computed<OrderRefundDraft | null>(() => {
	const { orderId, ticketIds } = orderRefundSelection.value;
	const eventId = consoleEventId.value;
	if (!orderId || !eventId) return null;
	const order = consoleOrders.value.find((o) => o.id === orderId);
	if (order?.status !== "paid" || order.origin === "guest") return null;
	// Every consequence below is per ticket, so it waits for the whole set.
	if (consoleTicketsStatus.value === "loading") return null;
	if (!consoleRowsAnswered(consoleTicketsStatus.value))
		return { kind: "ticketsUnreadable", order };

	const ledger = orderRefundLedger.value;
	const ledgerRows = ledger?.orderId === orderId ? ledger.rows : null;
	const held = new Set((ledgerRows ?? []).flatMap((r) => r.ticketIds));

	const tickets = consoleTickets.value.filter((t) => t.orderId === orderId);
	const shares = Order.perTicketShares(order, tickets);
	// The same derivation over the same order with its discount lines dropped —
	// which is exactly what "the price printed on the ticket" means, down to the
	// floor-and-remainder rule that decides which seat of a line carries the odd
	// cent. Deriving it any other way would be a second authority on one figure,
	// free to disagree with the first about that cent.
	const faceShares = Order.perTicketShares(
		{
			lines: order.lines.filter((line) => line.kind !== "discount"),
			voucher: null,
		},
		tickets,
	);
	const headroomCents = headroomOf(order, ledgerRows ?? []);
	// Why each ticket cannot be part of a refund, or null where it can — derived
	// before any money is, because the seats carrying no refusal are exactly the
	// ones the order's remaining headroom is shared out between.
	const refusals = new Map<string, RefundTicketRefusal | null>(
		tickets.map((t) => [
			t.id,
			t.status !== "valid"
				? t.status
				: held.has(t.id)
					? "claimed"
					: shares.has(t.id)
						? null
						: "unpriced",
		]),
	);
	// A ROW NEVER PRINTS MONEY THAT CANNOT MOVE: a refund settled at a seat's
	// PRINTED price drew on money the seats behind it were owed, so their own
	// pro-rata shares stopped being realisable. What is left apportions across the
	// seats a refund can still reach in proportion to those shares — floor, odd
	// cents one apiece to the largest remainders, ties to the lower ticket id,
	// which is `Order.apportionByLine`'s rule spent per ticket (that function
	// folds a line's seats into one entry, so it cannot answer this). Proportional
	// rather than first-come, so a seat's figure never turns on which other seat
	// is chosen. Where the headroom covers every share it is the identity, to the
	// cent, so every order nobody over-drew prices exactly as it always did.
	const selectable = tickets
		.filter((t) => refusals.get(t.id) === null)
		.sort((x, y) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
	const worth = selectable.reduce((sum, t) => sum + (shares.get(t.id) ?? 0), 0);
	const payable = Math.min(headroomCents, worth);
	const apportioned = selectable.map((t) => {
		const exact = worth > 0 ? (payable * (shares.get(t.id) ?? 0)) / worth : 0;
		return { id: t.id, cents: Math.floor(exact), remainder: exact % 1 };
	});
	let residue = payable - apportioned.reduce((sum, s) => sum + s.cents, 0);
	for (const seat of [...apportioned].sort(
		(x, y) => y.remainder - x.remainder,
	)) {
		if (residue <= 0) break;
		seat.cents += 1;
		residue -= 1;
	}
	const realisable = new Map(apportioned.map((s) => [s.id, s.cents]));

	const chosen = new Set(ticketIds);
	const rows = new Map<string, RefundTicketRow>(
		tickets.map((t) => {
			const refusal = refusals.get(t.id) ?? null;
			return [
				t.id,
				{
					ticket: t,
					// A refused seat is no claimant on the headroom, so it keeps the
					// pro-rata figure it was priced at; nothing offers it back.
					share: realisable.get(t.id) ?? shares.get(t.id) ?? null,
					faceShare: faceShares.get(t.id) ?? null,
					refusal,
					selected: refusal === null && chosen.has(t.id),
				},
			];
		}),
	);

	const selected = [...rows.values()].filter((r) => r.selected);
	// The rows are the money: this is their plain sum, so the button and the seats
	// under it can never state two different figures for one gesture.
	const payoutCents = selected.reduce((sum, r) => sum + (r.share ?? 0), 0);
	const block: RefundBlock | null =
		ledgerRows === null
			? "ledgerPending"
			: selected.length === 0
				? "nothingSelected"
				: null;
	const pricing = Order.refundPricing(order);
	// Every seat open, every one chosen, and the money going back the whole of
	// what came in — so the assurance is withheld from an order already holding a
	// seat it can never return, and from one whose seats were scaled down.
	const wholeOrder =
		selectable.length === rows.size &&
		selected.length === rows.size &&
		payoutCents === orderTotal(order);
	return {
		kind: "composing",
		order,
		rows,
		selectedCount: selected.length,
		selectableCount: selectable.length,
		// Every selected row is a selectable one by construction, so equal counts
		// is the whole test.
		allSelectableSelected:
			selectable.length > 0 && selected.length === selectable.length,
		pricing,
		payoutCents,
		headroomCents,
		footnote:
			wholeOrder && pricing.kind === "voucherNet"
				? "wholeOrderReconciles"
				: null,
		block,
		// The entry carries what the rows promised as its `amount`, so the consumer's
		// own clamp is a ceiling that never binds rather than a second opinion: a
		// SUBSET of an over-drawn order's seats would otherwise move more than the
		// rows the operator read. Null rather than 0 on a comp — a zero-priced
		// working set is a revocation, and the contract refuses a zero `amount`.
		selection:
			block === null
				? [
						{
							eventId,
							orderId,
							ticketIds: selected.map((r) => r.ticket.id),
							amount: payoutCents > 0 ? payoutCents : null,
						},
					]
				: [],
	};
});

/** Add or drop one ticket, refusing every row the draft marks unselectable —
 *  a redeemed ticket fails the whole marker server-side, so it must be
 *  unreachable here rather than submittable and refused. */
export function toggleRefundTicket(ticketId: string): void {
	const draft = orderRefundDraft.get();
	if (draft?.kind !== "composing") return;
	const row = draft.rows.get(ticketId);
	if (!row || row.refusal !== null) return;
	const { orderId, ticketIds } = orderRefundSelection.value;
	orderRefundSelection.replace({
		orderId,
		ticketIds: row.selected
			? ticketIds.filter((id) => id !== ticketId)
			: [...ticketIds, ticketId],
	});
}

/** Take or drop the whole order in one write. A block order carries up to
 *  `Event.MAX_TICKETS_PER_ORDER` tickets and refunding it was one tap each,
 *  every one of them re-deriving the entire draft — so the affordance is what
 *  makes the flow linear, and it names the whole set in a single
 *  `orderRefundSelection` write rather than looping the per-ticket toggle.
 *  Rows the draft refuses stay out of it on both paths: they are unreachable
 *  by hand for the reason `toggleRefundTicket` states, and a gesture that
 *  named them would submit a marker the server fails whole. */
export function toggleAllRefundTickets(): void {
	const draft = orderRefundDraft.get();
	if (draft?.kind !== "composing") return;
	orderRefundSelection.replace({
		orderId: draft.order.id,
		ticketIds: draft.allSelectableSelected
			? []
			: [...draft.rows.values()]
					.filter((r) => r.refusal === null)
					.map((r) => r.ticket.id),
	});
}

/** Every paid, non-guest order of the mounted event as one batch — no ticket
 *  selection, so each entry names its order alone and the consumer takes every
 *  ticket of it. Guest orders are excluded for the same reason
 *  `orderRefundDraft` excludes them: staff-issued, no real captured payment to
 *  give back. `redeemedOrders` is the honest caveat this surface owes: an
 *  order one of whose tickets the door already admitted fails its marker
 *  whole, because a refund that rewrote that admission would erase the only
 *  record of it — so its money and its seats are excluded from the three
 *  consequence figures while its entry stays in the selection, where its
 *  refusal is counted.
 *
 *  `ticketsUnreadable` is the same refusal `orderRefundDraft` carries and for
 *  the same reason: every consequence this surface states is derived from the
 *  event's tickets, which ride a different permission from the orders beside
 *  them. */
export type EventRefundDraft =
	| {
			readonly kind: "composing";
			readonly orderCount: number;
			readonly ticketCount: number;
			readonly totalCents: number;
			readonly redeemedOrders: number;
			readonly selection: readonly RefundSelectionEntry[];
	  }
	| { readonly kind: "ticketsUnreadable"; readonly orderCount: number };

export const eventRefundDraft = computed<EventRefundDraft | null>(() => {
	const eventId = consoleEventId.value;
	if (!eventId || !consoleRowsAnswered(consoleOrdersStatus.value)) return null;
	const orders = consoleOrders.value.filter(
		(o) => o.status === "paid" && o.origin !== "guest",
	);
	if (orders.length === 0) return null;
	if (consoleTicketsStatus.value === "loading") return null;
	if (!consoleRowsAnswered(consoleTicketsStatus.value))
		return { kind: "ticketsUnreadable", orderCount: orders.length };

	const paidOrderIds = new Set(orders.map((o) => o.id));
	const tickets = consoleTickets.value.filter((t) =>
		paidOrderIds.has(t.orderId),
	);
	const redeemedOrderIds = new Set(
		tickets.filter((t) => t.status === "redeemed").map((t) => t.orderId),
	);
	return {
		kind: "composing",
		orderCount: orders.length,
		ticketCount: tickets.filter(
			(t) => t.status === "valid" && !redeemedOrderIds.has(t.orderId),
		).length,
		totalCents: orders
			.filter((o) => !redeemedOrderIds.has(o.id))
			.reduce(
				(sum, o) => sum + Math.max(0, orderTotal(o) - (o.refundedAmount ?? 0)),
				0,
			),
		redeemedOrders: redeemedOrderIds.size,
		selection: orders.map((o) => ({ eventId, orderId: o.id })),
	};
});

/** The one refund write-intent channel — a single order's chosen tickets and a
 *  whole event's orders are the same seam, because a batch of one is how this
 *  system spells a single refund. `web`'s order-ops.sync.ts is the
 *  sole answerer: it mints the batchId, creates the document, and listens. */
export const refundBatchRequested$ = new Subject<
	readonly RefundSelectionEntry[]
>();

/** What became of the most recent refund request — the outcome half of
 *  `refundBatchRequested$`'s pair, so a surface reports a refund started only
 *  once the write was actually accepted and has somewhere to say so when it was
 *  not. One cell rather than a create result beside a progress counter: the two
 *  read together on every surface, and split they can disagree about which
 *  batch they describe.
 *
 *  `orderCount` is the bound the batch document itself does not carry — the
 *  count of distinct orders in the selection, which is the marker count by
 *  construction — so the client that composed the selection is the one place it
 *  can be read from. `watched` says whether the progress listener is still
 *  answering: the batch runs server-side either way, but counters that have
 *  stopped moving must not read as a batch that has stalled.
 *
 *  The cell outlives any one surface, so it is reset to `sending` at the top of
 *  every request — a batch that has not started can then never be read through
 *  the previous batch's finished counters. Written only by order-ops.sync.ts. */
export type RefundBatchOutcome =
	| { readonly kind: "sending" }
	| { readonly kind: "refused" }
	| {
			readonly kind: "accepted";
			readonly batchId: string;
			readonly orderCount: number;
			readonly done: number;
			readonly failed: number;
			readonly watched: boolean;
	  };

export const refundBatchOutcome = state<RefundBatchOutcome | null>(
	"order-ops/refund-batch-outcome",
).memory(null);
